import { CalendarDate, parseDate } from '@internationalized/date'
import dayjs from 'dayjs'
import 'dayjs/locale/sl'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import relativeTime from 'dayjs/plugin/relativeTime'
import utc from 'dayjs/plugin/utc'

dayjs.extend(localizedFormat)
dayjs.extend(relativeTime)
dayjs.extend(utc)

export default dayjs

/**
 * Point Day.js at the operator's language.
 *
 * Only `fromNow` reads it — `formatDate` and `formatDateTime` below use explicit `DD.MM.YYYY`
 * patterns, which are the panel's one date shape in every locale — but that one is the whole reason
 * the `sl` locale is imported above: *"3 hours ago"* on an issue whose severity, message and type
 * are all in Slovenian is the kind of half-translation §9.5 is written against. Day.js's own `sl`
 * locale carries the four plural forms Slovenian needs, which is why relative time is expressed
 * through it rather than assembled from locale keys of ours.
 *
 * Unknown locales fall back to `en`, the same fallback `libs/i18n` declares.
 */
export const setDayjsLocale = (locale: string): void => {
    dayjs.locale(locale === 'sl' ? 'sl' : 'en')
}

// The initial choice, read the way `libs/i18n/index.ts` reads it — from the same key, at module
// load, before any screen renders a date. `stores/app.ts` calls the setter on every later change.
setDayjsLocale(localStorage.getItem('locale') || 'sl')

export const formatDate     = (value?: string | null): string => value ? dayjs(value).format('DD.MM.YYYY') : ''
export const formatDateTime = (value?: string | null): string => value ? dayjs(value).format('DD.MM.YYYY HH:mm') : ''
/**
 * Relative time — *"3 hours ago"*, or with `withoutSuffix` the bare span *"3 hours"*.
 *
 * The second form is what the issues surfaces use (§7.1): an issue's `since` is read as *how long
 * this has been going on*, so the sentence around it supplies the tense — `issues.since` is
 * *"for {ago}"* / *"že {ago}"* — and Day.js supplies only the localized, correctly pluralized span.
 * Composing "for" with `fromNow`'s own suffix would produce "for 3 hours ago".
 */
export const fromNow = (value?: string | null, withoutSuffix = false): string =>
    value ? dayjs(value).fromNow(withoutSuffix) : ''

/**
 * `null` for a timestamp that means "never", including **Go's zero time**.
 *
 * Not every "never" arrives as `null`. A Go `time.Time` that is a value rather than a pointer
 * marshals to `0001-01-01T00:00:00Z`, and `instance_states.last_seen_at` is exactly that (§5.1's
 * row is created by the panel before any agent has ever called home). Rendered raw it reads
 * "01.01.0001" and relative-formats as "2025 years ago", which looks like a bug in the panel
 * rather than like an instance nobody has installed yet.
 *
 * The cutoff is the year 2000 rather than the Unix epoch so that a zero `time.Time`, a zero
 * `time.Time` shifted by a timezone, and a genuinely unset epoch date all collapse to the same
 * answer. Nothing this product records predates it.
 */
export const nonZeroTime = (value?: string | null): string | null => {
    if (!value) return null

    const moment = dayjs(value)

    return moment.isValid() && moment.year() >= 2000 ? value : null
}

/**
 * Nuxt UI date components model their value as `@internationalized/date` objects, never a JS
 * `Date`. Convert at the component boundary and keep API payloads as ISO strings.
 */
export const toCalendarDate = (value?: string | null): CalendarDate | null => {
    if (!value) return null
    const iso = dayjs(value).format('YYYY-MM-DD')
    return parseDate(iso) as CalendarDate
}

export const fromCalendarDate = (value?: CalendarDate | null): string => value ? value.toString() : ''
